import { useState, useRef, useEffect, useCallback, type MutableRefObject } from 'react';
import { snapshotDrop, collectFromSnapshot } from '../utils/drop-walk';
import { getVerifiedTenantBrowserKey } from '../services/tenant-runtime-scope';
import { isQuoteRunActiveError } from '../services/quote-run-open-guard';

interface UseGlobalDashboardDropOptions {
    currentPage: string;
    stage: number;
    resetForm: () => void;
    handleNavigate: (page: string) => void;
}

interface UseGlobalDashboardDropResult {
    globalDragActive: boolean;
    stage1Ref: MutableRefObject<any>;
}

export function useGlobalDashboardDrop({
    currentPage,
    stage,
    resetForm,
    handleNavigate,
}: UseGlobalDashboardDropOptions): UseGlobalDashboardDropResult {
    const [globalDragActive, setGlobalDragActive] = useState(false);
    const globalDragCounter = useRef(0);
    const stage1Ref = useRef<any>(null);
    const pendingDropFilesRef = useRef<File[] | null>(null);
    const currentPageRef = useRef(currentPage);
    useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

    const dropDepsRef = useRef({ resetForm, handleNavigate });
    useEffect(() => { dropDepsRef.current = { resetForm, handleNavigate }; });

    useEffect(() => {
        const isDashboardNow = () => currentPageRef.current === 'dashboard';

        const handleDragEnter = (e: DragEvent) => {
            if (!isDashboardNow()) return;
            if (!e.dataTransfer || !e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            globalDragCounter.current++;
            if (globalDragCounter.current === 1) setGlobalDragActive(true);
        };
        const handleDragLeave = (e: DragEvent) => {
            if (!isDashboardNow()) return;
            e.preventDefault();
            globalDragCounter.current = Math.max(0, globalDragCounter.current - 1);
            if (globalDragCounter.current === 0) setGlobalDragActive(false);
        };
        const handleDragOver = (e: DragEvent) => {
            if (!isDashboardNow()) return;
            if (!e.dataTransfer || !e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            try { e.dataTransfer.dropEffect = 'copy'; } catch {}
        };
        const handleDrop = (e: DragEvent) => {
            if (!isDashboardNow()) return;
            e.preventDefault();
            globalDragCounter.current = 0;
            setGlobalDragActive(false);
            const snap = snapshotDrop(e.dataTransfer, { killSwitchKey: 'qm_disable_global_drop_walk' });
            if (snap.entries.length === 0 && snap.fallbackFiles.length === 0) return;
            (async () => {
                const files = await collectFromSnapshot(snap);
                if (!files || files.length === 0) return;
                // Reset FIRST. A Quote Run refuses the reset (decision D1,
                // 2026-09-28) and shows its own message. Nothing may be queued
                // or cleared before that answer: queued files would otherwise
                // wait for the next Stage 1 on screen, which during a run is
                // the queue's next job.
                try {
                    dropDepsRef.current.resetForm();
                } catch (err) {
                    if (isQuoteRunActiveError(err)) return;
                    throw err;
                }
                pendingDropFilesRef.current = files;
                try {
                    const QM_STATE_KEY = getVerifiedTenantBrowserKey('qm_last_view_state');
                    if (QM_STATE_KEY) sessionStorage.removeItem(QM_STATE_KEY);
                } catch {}
                dropDepsRef.current.handleNavigate('quotemaker');
            })();
        };

        const prev = typeof window !== 'undefined' ? (window as any).__qm_drop_handlers : null;
        if (prev) {
            try {
                window.removeEventListener('dragenter', prev.handleDragEnter);
                window.removeEventListener('dragleave', prev.handleDragLeave);
                window.removeEventListener('dragover', prev.handleDragOver);
                window.removeEventListener('drop', prev.handleDrop);
            } catch {}
        }
        (window as any).__qm_drop_handlers = { handleDragEnter, handleDragLeave, handleDragOver, handleDrop };
        window.addEventListener('dragenter', handleDragEnter);
        window.addEventListener('dragleave', handleDragLeave);
        window.addEventListener('dragover', handleDragOver);
        window.addEventListener('drop', handleDrop);
        return () => {
            window.removeEventListener('dragenter', handleDragEnter);
            window.removeEventListener('dragleave', handleDragLeave);
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('drop', handleDrop);
            if (typeof window !== 'undefined' && (window as any).__qm_drop_handlers?.handleDrop === handleDrop) {
                (window as any).__qm_drop_handlers = null;
            }
        };
    }, []);

    const drainQueuedStage1Files = useCallback(() => {
        let attempts = 0;
        const tryDrain = () => {
            if (!pendingDropFilesRef.current) return;
            if (stage1Ref.current?.processDroppedFiles) {
                const files = pendingDropFilesRef.current;
                pendingDropFilesRef.current = null;
                try { stage1Ref.current.processDroppedFiles(files); } catch {}
                return;
            }
            if (++attempts < 40) setTimeout(tryDrain, 50);
        };
        tryDrain();
    }, []);

    useEffect(() => {
        if (currentPage !== 'quotemaker' || stage !== 1) return;
        if (!pendingDropFilesRef.current) return;
        drainQueuedStage1Files();
    }, [currentPage, stage, drainQueuedStage1Files]);

    return { globalDragActive, stage1Ref };
}
